export type TimeUnit = "minutes" | "hours" | "days" | "weeks" | "months";

export type CharacterTier = "free" | "premium" | "patron";

export interface SequenceStep {
  id: string;
  label: string;
  offsetValue: number;
  offsetUnit: TimeUnit;
  sendTimeOptionId?: string;
  prompt: string;
}

export interface TrackingSettings {
  trackOpens: boolean;
  trackClicks: boolean;
  trackChatEngagement: boolean;
  trackReplies: boolean;
}

export type CharacterSelectionStrategy =
  | "last-high-token"
  | "most-frequent"
  | "ai-assisted";

export interface ThreadFollowUpSettings {
  dynamicRange: {
    minTime: number;
    maxTime: number;
    unit: "hours" | "minutes";
  };
}

export interface CampaignSettings {
  sequence: SequenceStep[];
  threadFollowUp: ThreadFollowUpSettings;
  tokenThreshold: number;
  defaultCharacterId: string;
  sendingAddress: string;
  mailProviderName: string;
  tracking: TrackingSettings;
  characterStrategy: CharacterSelectionStrategy;
}

export interface PromptTemplate {
  id: string;
  title: string;
  description: string;
  prompt: string;
  lastUpdated: string;
}

export interface ManualTemplate {
  id: string;
  title: string;
  description: string;
  body: string;
  lastUpdated: string;
  schedule?: SendSchedule;
}

export interface NoChatPromptRule {
  id: string;
  label: string;
  preview?: string;
  enabled: boolean;
}

export interface NoChatScenario {
  id: string;
  characterId: string;
  from_character_id?: string;
  storylineTitle: string;
  storylineSummary?: string;
  prompts: NoChatPromptRule[];
  subject: string;
  body: string;
  promptInstructions?: string;
  manualEmailEnabled?: boolean;
  manualEmailSubject?: string;
  manualEmailBody?: string;
  lastUpdated: string;
  maxConversationCount?: number | null;
}

export interface NoChatTemplate extends ManualTemplate {
  triggerScenarios: NoChatScenario[];
}

export interface CharacterPrompt {
  campaignId: string;
  campaignName: string;
  subject: string;
  body: string;
  lastUpdated: string;
  targetCharacterIds: string[];
  schedule?: SendSchedule;
}

export type SendTimeReference =
  | "last-chat-start"
  | "last-chat-end"
  | "user-morning"
  | "user-afternoon"
  | "user-evening"
  | "user-night"
  | "signup"
  | "custom";

export interface SendTimeOption {
  id: string;
  label: string;
  value: string;
  reference: SendTimeReference;
  offsetValue?: number;
  offsetUnit?: TimeUnit;
  description?: string;
}

export const DEFAULT_SEND_TIME_VALUE = "use-default-timing";

export interface SendSchedule {
  offsetValue: number;
  offsetUnit: TimeUnit;
  sendTimeOptionId?: string;
}

export type UserActivityStatus =
  | "active"
  | "inactive"
  | "churned"
  | "no-chat"
  | "trial"
  | "subscriber";

export interface CampaignUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  lastInteractionAt: string;
  timezone: string;
  characters: Array<{
    characterId: string;
    name: string;
    tokensUsed: number;
    tier: CharacterTier;
  }>;
  totalTokens: number;
  totalConversations?: number;
  status: UserActivityStatus;
  badges: string[];
  currentSequenceStep?: number;
  lastEmailSentAt?: string;
  isPaidSubscriber?: boolean;
  membershipBadge?: string;
  tokenBadge?: string;
  ranOutOfTokens?: boolean;
  everRanOutOfTokens?: boolean;
  campaignId?: string;
  campaignName?: string;
  campaigns?: Array<{
    campaignId: string;
    campaignName: string;
    lastSentAt?: string;
  }>;
}

export type CampaignUserSortOption = "latest" | "conversations";

export type CampaignUserFilterKey = "paidUsersOnly" | "ranOutOfTokensOnly";

export interface CampaignUserFilters {
  sort: CampaignUserSortOption;
  paidUsersOnly: boolean;
  ranOutOfTokensOnly: boolean;
  minTokens?: number | null;
  maxTokens?: number | null;
  fromDate?: string | null;
  toDate?: string | null;
  maxConversationCount?: number | null;
  minUniqueCharacters?: number | null;
  requiredCharacterIds?: string[];
}

export interface BatchConfiguration {
  id: string;
  name: string;
  promptId: string;
  sequenceId: string;
  sendTimeOptionId: string;
  createdAt: string;
  createdBy: string;
  notes?: string;
}

export interface BatchResultMetrics {
  openRate: number;
  clickRate: number;
  reengagementRate: number;
  replyRate: number;
}

export type EmailAutomationFeature =
  | "reengagement-email-sequence"
  | "no-chat-email"
  | "premium-triggers";

export interface BatchRun {
  id: string;
  configurationId: string;
  sendTimeOptionId?: string;
  label: string;
  userCount: number;
  status: "scheduled" | "executing" | "completed";
  startedAt?: string;
  completedAt?: string;
  metrics?: BatchResultMetrics;
  features: EmailAutomationFeature[];
}

export type ScheduledEmailJobType = "sequence" | "reachout" | "no-chat-intro" | "email-reply";

export type ScheduledEmailStatus =
  | "pending"
  | "executing"
  | "completed"
  | "failed"
  | "cancelled"
  | "sent";

export interface ScheduledEmail {
  id: string;
  userId: string;
  email: string;
  createdAt?: string;
  userTimezone?: string;
  scheduledFor: string;
  scheduledForLocal?: string;
  jobType: ScheduledEmailJobType;
  sequenceIndex?: number;
  emailSubject?: string;
  status: ScheduledEmailStatus;
  campaignId?: string;
  campaignName?: string;
  // Legacy fields retained for mock/sample data support
  characterName?: string;
  sequenceStepName?: string;
  promptTitle?: string;
  timezone?: string;
  sequenceStepIndex?: number;
}

export interface PremiumCharacterRule {
  id: string;
  reachoutCharacterId: string;
  reachoutCharacterTier?: string;
  triggerCharacterIds: string[];
  minimumTokens: number;
  triggerAfterAnyCount?: number;
  resendCooldownDays: number;
  sendWindow: SendSchedule;
  promptInstructions?: string;
  manualEmailEnabled?: boolean;
  manualEmailSubject?: string;
  manualEmailBody?: string;
  serverRuleId?: string;
}

export interface PremiumCriteria {
  characterRules: PremiumCharacterRule[];
}

export interface AnalyticsSnapshot {
  totalEmailsSent: number;
  openRate: number;
  clickRate: number;
  reengagementRate: number;
  replyRate: number;
  weeklySends: number;
}

export interface SequencePerformance {
  stepId: string;
  stepLabel: string;
  openRate: number;
  clickRate: number;
  reengagementRate: number;
}

export interface CampaignCharacter {
  id: string;
  name: string;
  tier: CharacterTier;
}

export interface CharacterPerformance {
  characterId: string;
  characterName: string;
  tier: CharacterTier;
  sends: number;
  openRate: number;
  reengagementRate: number;
}

export interface CampaignMetricsEntry {
  campaignId: string;
  campaignName: string;
  features?: {
    reengagementSequence?: boolean;
    noChatIntro?: boolean;
    reachout?: boolean;
    emailReply?: boolean;
    [key: string]: boolean | undefined;
  };
  audienceCount: number;
  sent: number;
  opened: number;
  clicked: number;
  unsubscribed: number;
  replied: number;
  openRate: number;
  clickRate: number;
  unsubscribeRate: number;
  replyRate: number;
}

export interface EmailCampaignState {
  settings: CampaignSettings;
  promptLibrary: {
    noChat: NoChatTemplate;
    premium: PromptTemplate;
    sendTimeOptions: SendTimeOption[];
  };
  characterLibrary: CampaignCharacter[];
  batchConfigurations: BatchConfiguration[];
  batchRuns: BatchRun[];
  users: CampaignUser[];
  scheduledEmails: ScheduledEmail[];
  premiumCriteria: PremiumCriteria;
  analytics: {
    overview: AnalyticsSnapshot;
    sequencePerformance: SequencePerformance[];
    characterPerformance: CharacterPerformance[];
  };
}

export type CampaignLifecycleStatus = "active" | "paused" | "draft";

export interface ManagedEmailCampaign {
  id: string;
  name: string;
  status: CampaignLifecycleStatus;
  data: EmailCampaignState;
}

export type CampaignSummary = Pick<
  ManagedEmailCampaign,
  "id" | "name" | "status"
>;
